"use client";

/**
 * Live Preview Listener Component
 *
 * Client component that listens for Payload CMS live preview changes
 * and triggers a router refresh when content is updated.
 *
 * IMPORTANT: Uses window.location.origin to avoid postMessage CORS errors
 * when admin panel and preview are on different domains (e.g., Vercel preview
 * vs production domain).
 *
 * This approach works with server components (unlike useLivePreview)
 * because it doesn't try to render the content - it just refreshes the page.
 *
 * NOTE: We intentionally do NOT use RefreshRouteOnSave from @payloadcms/live-preview-react
 * because it calls router.refresh() immediately on mount. The initial server render already
 * fetches draft data (draft: isDraft), so that immediate refresh is redundant and causes
 * "Error in input stream" / CLIENT EXCEPTION ERROR flashes in preview mode.
 */

import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

// Message type strings from @payloadcms/live-preview (verified against v3.x).
// These are inlined to avoid the library's on-mount router.refresh() — see NOTE above.
const PAYLOAD_DOCUMENT_EVENT = "payload-document-event";
const PAYLOAD_LIVE_PREVIEW_READY = "payload-live-preview";

export function LivePreviewListener() {
	const router = useRouter();
	const hasSentReadyMessage = useRef(false);

	// Use window.location.origin to avoid postMessage CORS errors when admin
	// panel and preview are on different domains (e.g. Vercel preview vs production).
	// Initialise as undefined so server and initial client render both return null
	// (avoiding hydration mismatch), then set after mount.
	const [origin, setOrigin] = useState<string | undefined>(undefined);

	useEffect(() => {
		setOrigin(window.location.origin);
	}, []);

	useEffect(() => {
		if (!origin) return;

		// Inline isDocumentEvent logic from @payloadcms/live-preview
		const onMessage = (event: MessageEvent) => {
			if (
				event.origin === origin &&
				event.data &&
				typeof event.data === "object" &&
				(event.data as Record<string, unknown>).type === PAYLOAD_DOCUMENT_EVENT
			) {
				router.refresh();
			}
		};

		window.addEventListener("message", onMessage);

		// Send ready signal to Payload admin — inline ready() from @payloadcms/live-preview.
		// Supports both iframe (window.parent) and popup (window.opener) preview modes.
		if (!hasSentReadyMessage.current) {
			hasSentReadyMessage.current = true;
			const windowToPostTo = window.opener || window.parent;
			windowToPostTo?.postMessage(
				{ type: PAYLOAD_LIVE_PREVIEW_READY, ready: true },
				origin,
			);
			// Intentionally NOT calling router.refresh() here.
			// The initial server render already fetches draft content (draft: isDraft),
			// so an immediate refresh is redundant and causes CLIENT EXCEPTION ERROR flashes.
		}

		return () => {
			window.removeEventListener("message", onMessage);
		};
	}, [origin, router]);

	return null;
}
